import { animated, config, useSpring } from '@react-spring/web';
import { ColorBrandBlue1, Spacing16, Spacing4 } from 'autogen/design-tokens/tokens';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import { Typography } from 'components/generic/typography';
import HelperText from 'components/shared/inputs/components/HelperText';
import { InvalidInputFocusStyle, InvalidInputStyle, ValidInputDisabledStyle } from 'components/shared/inputs/theme';
import React, { useId, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ControlledToggle } from '../shared/types';

/* #region  styles */

const Input = styled.input`
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
`;

const Label = styled.label`
    margin: 0;
    display: inline-block;
`;

const Container = styled(Flex.Column)`
    width: fit-content;
`;

const LabelContainer = styled.label`
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing16};
`;

const StaticContainer = styled.div`
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing16};
`;

const ToggleShell = styled.div<{
    $isActive: boolean;
    $padding: string;
    $width: string;
    $isInvalid?: boolean;
    $disabled?: boolean;
}>`
    position: relative;
    display: flex;
    flex: none;
    align-items: center;
    padding: ${({ $padding }) => $padding};
    border-radius: 25px;
    width: ${({ $width }) => $width};
    transition: 250ms background-color ease;
    margin: unset;
    cursor: pointer;
    border-width: 1px;
    border-style: solid;
    border-color: ${({ theme, $isActive }) => ($isActive ? theme.ColorBackgroundBrand : theme.ColorBorderPrimary)};
    background-color: ${({ theme, $isActive }) => ($isActive ? theme.ColorBackgroundBrand : theme.ColorBackgroundPrimary)};
    
    &:hover {
        border-color: ${({ theme }) => theme.ColorBorderHover};
    }

    &:has(${Input}:focus), ${Input}:focus + & {
        outline: solid 2px ${ColorBrandBlue1};
        outline-offset: ${Spacing4};
    }

    ${({ $isInvalid, $isActive, theme }) =>
        $isInvalid &&
        css`
            &&,
            &&:hover {
                ${InvalidInputStyle};
            }

            &&:focus {
                ${InvalidInputFocusStyle};
            }

            background-color: ${$isActive ? theme.ColorBackgroundError : theme.ColorBackgroundPrimary};
        `};

        ${({ $disabled }) => $disabled && ValidInputDisabledStyle};
`;

const Knob = styled.div<{ $isActive: boolean; $size: string; $disabled?: boolean; $isInvalid?: boolean }>`
    position: relative;
    width: ${({ $size }) => $size};
    height: ${({ $size }) => $size};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    /* antialiasing */
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transform: translateZ(0);
    will-change: transform;
    -webkit-perspective: 1000;
    perspective: 1000;

    & svg {
        fill: ${({ theme, $isInvalid, $disabled }) => (!$isInvalid ? ($disabled ? theme.ColorIconDisabled : theme.ColorIconBrand) : theme.ColorIconError)}
    }

    background-color: ${({ theme, $isActive, $disabled }) => {
        if ($disabled) return $isActive ? theme.ColorIconBrandInverted : theme.ColorIconDisabled;
        return $isActive ? theme.ColorIconBrandInverted : theme.ColorIconInactive;
    }};


`;

const AnimatedKnob = animated<any>(Knob);

const AnimatedContainer = styled<any>(animated.div)`
    display: flex;
`;
/* #endregion */

/**
 * Switch - UI component (Does not implement state)
 */
const ControlledToggleButton = React.forwardRef<HTMLInputElement, ControlledToggle>((props, ref) => {
    const { active, onChange, size = 'large', name, readOnly, disabled, isInvalid, helperText, label } = props;
    const helperTextId = useId();

    const switchSizes = {
        large: {
            knobSize: '24px',
            switchSize: '52px',
            padding: '4px'
        },
        small: {
            knobSize: '16px',
            switchSize: '40px',
            padding: '4px'
        }
    };

    const [onHover, setOnHover] = useState(false);

    const transX = {
        on: Number.parseInt(switchSizes[size].knobSize) - Number.parseInt(switchSizes[size].padding),
        off:
            0.8 * Number.parseInt(switchSizes[size].knobSize) +
            (Number.parseInt(switchSizes[size].padding) - Number.parseInt(switchSizes[size].knobSize))
    };

    const knobTransition = useSpring({
        x: active ? transX.on : transX.off,
        scale: active || onHover ? 1 : 0.8,
        config: config.stiff
    });
    const checkmarkTransition = useSpring({ scale: active ? 1 : 0, config: config.stiff });

    const baseProps = {
        $width: switchSizes[size].switchSize,
        $padding: switchSizes[size].padding,
        $isActive: active,
        $isInvalid: isInvalid,
        $disabled: disabled
    };

    const interactiveProps = {
        onMouseEnter: !disabled ? () => setOnHover(true) : undefined,
        onMouseLeave: !disabled ? () => setOnHover(false) : undefined
    };

    const baseToggle = (
        <ToggleShell {...baseProps}>
            <Input
                ref={ref}
                readOnly={readOnly}
                name={name}
                id={name}
                type="checkbox"
                checked={active}
                disabled={disabled}
                onChange={!readOnly ? (e) => onChange(e.target.checked, e) : undefined}
                aria-describedby={helperTextId}
            />
            <AnimatedKnob
                $size={switchSizes[size].knobSize}
                style={knobTransition}
                $isActive={active}
                $disabled={disabled}
                $isInvalid={isInvalid}
            >
                {size === 'large' ? (
                    <AnimatedContainer style={checkmarkTransition}>
                        <Icon type={!isInvalid ? 'Checkmark' : 'Minus'} size={16} />
                    </AnimatedContainer>
                ) : null}
            </AnimatedKnob>
        </ToggleShell>
    );

    if (label || helperText) {
        return (
            <Container gap={Spacing4}>
                {readOnly ? (
                    <LabelContainer {...interactiveProps}>
                        {label && <Typography.Label size="M">{label}</Typography.Label>}
                        {baseToggle}
                    </LabelContainer>
                ) : (
                    <StaticContainer>
                        {label && <Typography.Label size="M">{label}</Typography.Label>}
                        {baseToggle}
                    </StaticContainer>
                )}
                {helperText && <HelperText helperText={helperText} helperTextId={helperTextId} />}
            </Container>
        );
    }

    return readOnly ? baseToggle : <Label {...interactiveProps}>{baseToggle}</Label>;
});

export default ControlledToggleButton;
